
import useEmblaCarousel from 'embla-carousel-react';
import { BACKGROUND_COLOR } from 'interfaces';
import { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import devices from '@/utilities/devices';

// #region STYLE
const Container = styled.div`
	display: flex;
	justify-content: center;
	flex-direction: column;
	gap: 1.6rem;
	padding: 3.5rem 0 2rem;

	.firstLine {
		align-items: center;
		display: flex;
		gap: 2.5rem;
		justify-content: center;
	}

	.secondLine {
		align-items: center;
		display: flex;
		gap: 2.5rem;
		justify-content: center;
	}

	.is-draggable {
		cursor: move;
		cursor: grab;
	}
	.is-dragging {
		cursor: grabbing;
	}
	@media screen and (${devices.mobile}) {
		padding: 2rem 0;
	}
`;

const CarouselContainer = styled.div<{ backgroundColor: BACKGROUND_COLOR }>`
	display: flex;
	margin: 1rem;
	overflow: hidden;
	position: relative;
	user-select: none;
	:before{
		background: ${({ backgroundColor, theme }) => (
			backgroundColor === BACKGROUND_COLOR.beigeLight ? (
				`linear-gradient(to left, rgba(${theme.color.rgbWhite}, .15), ${theme.color.beigeLight});`
			) : (
				`linear-gradient(to left, rgba(${theme.color.rgbWhite}, .15), ${theme.color.white});`
			)
		)};
		content: '';
		display: block;
		height: 37px;
		left: 0;
		position: absolute;
		width: 3%;
		z-index: 100;
	}

	:after{
		background: ${({ backgroundColor, theme }) => (
			backgroundColor === BACKGROUND_COLOR.beigeLight ? (
				`linear-gradient(to right, rgba(${theme.color.rgbWhite}, .15), ${theme.color.beigeLight});`
			) : (
				`linear-gradient(to right, rgba(${theme.color.rgbWhite}, .15), ${theme.color.white});`
			)
		)};
		content: '';
		display: block;
		height: 37px;
		right: 0;
		position: absolute;
		width: 3%;
		z-index: 100;
	}

	.emblaCarousel-container {
		align-items: center;
		display: flex;
		gap: 0 4rem;
		padding: 0 4rem;
		margin-left: -14rem;
		img {
			height: 35px;
			max-height: 35px;
			width: auto;
		}
	}

	@media screen and (${devices.mobile}) {
		margin: 0;

		.emblaCarousel-content {
			margin: 0 1.2rem;
		}
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		margin: 0;

		.emblaCarousel-content {
			margin: 0 1.5rem;
		}
	}
`;
// #endregion

const CarouselInfinite = ({ backgroundColor, children }: { backgroundColor: BACKGROUND_COLOR; children: any; }) => {

	const [emblaRef, embla] = useEmblaCarousel({
		loop: true,
		skipSnaps: true,
		speed: 20,
	});
	const rafId = useRef(0);

	const animate = useCallback(
		() => {
			if (!embla || !rafId.current) {
				return;
			}

			const engine = embla.internalEngine();
			engine.location.add((window.screen.width > 1920) ? -0.5 : -1);
			engine.scrollLooper.loop(-1);
			engine.slideLooper.loop();
			engine.target.set(engine.location);
			engine.translate.to(engine.location);
			rafId.current = requestAnimationFrame(animate);
		},
		[embla],
	);

	const startAutoScroll = useCallback(
		() => {
			rafId.current = requestAnimationFrame(animate);
		},
		[animate],
	);

	const stopAutoScroll = useCallback(
		() => {
			rafId.current = cancelAnimationFrame(rafId.current) as any || 0;
		},
		[],
	);

	useEffect(
		() => {
			if (embla) {
				embla.on('pointerDown', stopAutoScroll);
				embla.on('pointerUp', () => embla.clickAllowed() && startAutoScroll());
				embla.on('settle', startAutoScroll);

				startAutoScroll();
			}
			return () => stopAutoScroll();
		},
		[embla, startAutoScroll, stopAutoScroll],
	);

	return (
		<Container>
			<CarouselContainer
				backgroundColor={backgroundColor}
				className='is-draggable'
				ref={emblaRef}
			>
				<div className='emblaCarousel-container'>
					{children}
				</div>
			</CarouselContainer>
		</Container>
	);
};

export default CarouselInfinite;
